import idx from 'idx';
import request from 'axios';
import partial from 'lodash/partial';
import isEqual from 'lodash/isEqual';
import differenceWith from 'lodash/differenceWith';
import { browserHistory } from 'react-router';
import { getProject, fetchProject } from 'project/ducks/project';
import { createAction, handleActions } from 'redux-actions';
import { setBannerError } from 'error';
import { getProjectUrl } from 'project/utils';
import { getSubCategory } from 'common/ducks/categories';
import { updateUser } from 'common/ducks/user';

/** ****************************************************************
 * Constants *******************************************************
 * ****************************************************************
 */
export const TOGGLE_ADD_MODAL = 'crowdspring/focusgroups/TOGGLE_ADD_MODAL';
export const TOGGLE_EDIT_ENTRIES = 'crowdspring/focusgroups/TOGGLE_EDIT_ENTRIES';
export const TOGGLE_ADD_PARTICIPANTS = 'crowdspring/focusgroups/TOGGLE_ADD_PARTICIPANTS';
export const SET_DETAILS = 'crowdspring/focusgroups/SET_DETAILS';
export const TOGGLE_SELECT_ENTRIES = 'crowdspring/focusgroups/TOGGLE_SELECT_ENTRIES';
export const SET_ENTRIES = 'crowdspring/focusgroups/SET_ENTRIES';
export const CHANGE_ADD_MODAL_STEP = 'crowdspring/focusgroups/CHANGE_ADD_MODAL_STEP';

export const REQ_CREATE_GROUP = 'crowdspring/focusgroups/REQ_CREATE_GROUP';
export const REC_CREATE_GROUP = 'crowdspring/focusgroups/REC_CREATE_GROUP';
export const ERR_CREATE_GROUP = 'crowdspring/focusgroups/ERR_CREATE_GROUP';

export const REQ_ALL_GROUPS = 'crowdspring/focusgroups/REQ_ALL_GROUPS';
export const REC_ALL_GROUPS = 'crowdspring/focusgroups/REC_ALL_GROUPS';
export const ERR_ALL_GROUPS = 'crowdspring/focusgroups/ERR_ALL_GROUPS';

export const REQ_FOCUS_GROUP = 'crowdspring/focusgroups/REQ_FOCUS_GROUP';
export const REC_FOCUS_GROUP = 'crowdspring/focusgroups/REC_FOCUS_GROUP';
export const ERR_FOCUS_GROUP = 'crowdspring/focusgroups/ERR_FOCUS_GROUP';

/** ****************************************************************
 * Action Creators *******************************************************
 */
export const toggleAddModal = createAction(TOGGLE_ADD_MODAL);
export const changeAddModalStep = createAction(CHANGE_ADD_MODAL_STEP);
export const toggleEditEntries = createAction(TOGGLE_EDIT_ENTRIES);
export const toggleAddParticipants = createAction(TOGGLE_ADD_PARTICIPANTS);

export const setDetails = createAction(SET_DETAILS);
export const setEntries = createAction(SET_ENTRIES);
export const toggleSelectedEntries = createAction(TOGGLE_SELECT_ENTRIES);

export const reqCreateGroup = createAction(REQ_CREATE_GROUP);
export const recCreateGroup = createAction(REC_CREATE_GROUP);
export const errCreateGroup = createAction(ERR_CREATE_GROUP);

export const reqAllGroups = createAction(REQ_ALL_GROUPS);
export const recAllGroups = createAction(REC_ALL_GROUPS);

export const reqFocusGroup = createAction(REQ_FOCUS_GROUP);
export const recFocusGroup = createAction(REC_FOCUS_GROUP);

export const fetchAll = projectId => (dispatch) => {
  dispatch(reqAllGroups());
  const promise = request.get(`/api/v1/focus-groups/?project=${projectId}`);
  promise.then(response => (
    dispatch(recAllGroups(response.data))
  )).catch(() => {});
  return promise;
};

export const create = (project, participants) => (dispatch, getState) => {
  const { focusGroups } = getState().project;
  const { profile_data } = getState().user;
  const projectObj = getProject(getState());
  const subcategory = getSubCategory(getState().categories, projectObj.sub_category);
  const { entry_type } = projectObj;

  const { weight_votes, invite_message, users, first_name, last_name } = participants;
  const values = { ...focusGroups.viewState.details, project, weight_votes };

  dispatch(reqCreateGroup());

  request.post('/api/v1/focus-groups/', values).then((response) => {
    const groupId = response.data.id;
    const entries = focusGroups.viewState.entries;

    dispatch(updateUser({ id: profile_data.id, first_name, last_name }));

    /* fetch project with possibly updated `can_create_focus_group` field */
    dispatch(fetchProject(projectObj.id));

    const reqEntries = entries.map(revision => (
      partial(request.post, `/api/v1/focus-groups/${groupId}/${entry_type}-revisions/`, { revision })
    ));
    const reqParticipants = users.map(user => (
      partial(request.post, `/api/v1/focus-groups/${groupId}/participants/`, {
        ...user,
        invite_message,
      })
    ));

    request.all([...reqParticipants, ...reqEntries].map(req => (req()))).then(
      request.spread(() => {
        const projectUrl = getProjectUrl(
          projectObj,
          subcategory.slug,
          `focus-groups/${groupId}/view/results/comment`
        );
        dispatch(recCreateGroup());
        dispatch(fetchAll(projectObj.id));
        return browserHistory.push(projectUrl);
      })
    ).catch(() => (
      dispatch(
        setBannerError(
          'Focus Group Error',
          'Your focus group was created, but there was an error inviting participants and selecting revision'
        )
      )
    ));
  }).catch(e => (
    dispatch(errCreateGroup(e))
  ));
};

export const fetchGroup = (groupId, key, anonymousKey) => (dispatch) => {
  dispatch(reqFocusGroup());

  let config = null;
  if (key) {
    config = { headers: { 'X-Participant-Key': key } };
  } else if (anonymousKey) {
    config = { headers: { 'X-Anonymous-Key': anonymousKey } };
  }

  const participantRequests = [
    partial(request.get, `/api/v1/focus-groups/${groupId}/`, config),
    partial(request.get, `/api/v1/focus-groups/${groupId}/name-revisions/`, config),
    partial(request.get, `/api/v1/focus-groups/${groupId}/image-revisions/`, config),
  ];
  const ownerRequests = [
    partial(request.get, `/api/v1/focus-groups/${groupId}/participants/`),
  ];

  const requests = (
    key || anonymousKey ? participantRequests : [...participantRequests, ...ownerRequests]
  );

  return request.all(requests.map(req => (req()))).then(
    request.spread((focusGroupResponse, nameRevisions, imageRevisions, participants) => {
      const revisions = nameRevisions.data.length ? nameRevisions.data : imageRevisions.data;
      const type = nameRevisions.data.length ? 'name' : 'image';
      return dispatch(recFocusGroup({
        ...focusGroupResponse.data,
        type,
        revisions,
        participants: participants ? participants.data : [],
      }));
    })
  ).catch(() => (
    dispatch(setBannerError(
      'Focus Group Error',
      'There was an error retrieving focus groups.'
    ))
  ));
};


export const updateGroupEntries = () => (dispatch, getState) => {
  const { entry_type } = getProject(getState());
  const { id, revisions } = idx(getState(), _ =>
    _.project.focusGroups.viewState.currentGroup
  ) || { revisions: [], id: null };
  const newRevs = idx(getState(), _ => _.project.focusGroups.viewState.selected);
  const oldRevs = revisions.map(({ revision }) => (revision.id));

  const deleteRevs = differenceWith(oldRevs, newRevs, isEqual);
  const addRevs = differenceWith(newRevs, oldRevs, isEqual);

  const deleteReqs = deleteRevs.map((revId) => {
    const delRev = revisions.find(revision => (revision.revision.id === revId));
    return partial(request.delete, `/api/v1/focus-groups/${id}/${entry_type}-revisions/${delRev.id}/`);
  });
  const addReqs = addRevs.map(revision =>
    partial(request.post, `/api/v1/focus-groups/${id}/${entry_type}-revisions/`, { revision })
  );

  return request.all([...addReqs, ...deleteReqs].map(req => (req()))).then(
    request.spread(() => (dispatch(fetchGroup(id))))
  ).catch(() => (
    dispatch(setBannerError(
      'Focus Group Error',
      'There was an error updating the entries for your focus group'
    ))
  ));
};

export const updateGroupParticipants = ({ users, invite_message }) => (dispatch, getState) => {
  const { id } = idx(getState(), _ => _.project.focusGroups.viewState.currentGroup) || { id: null };
  const url = `/api/v1/focus-groups/${id}/participants/`;
  const addReqs = users.map(user => (partial(request.post, url, { ...user, invite_message })));

  return request.all([...addReqs].map(req => (req()))).then(
    request.spread(() => (dispatch(fetchGroup(id))))
  ).catch(() => (
    dispatch(setBannerError(
      'Focus Group Error',
      'There was an error adding participants to your focus group'
    ))
  ));
};

export const changeParticipant = (participantId, weight) => (dispatch, getState) => {
  const { id } = idx(getState(), _ => _.project.focusGroups.viewState.currentGroup) || { id: null };
  const url = `/api/v1/focus-groups/${id}/participants/${participantId}/`;
  return request.patch(url, { weight }).then(() =>
    dispatch(fetchGroup(id))
  ).catch(() => (
    dispatch(setBannerError(
      'Focus Group Error',
      'There was an error updating the participant for your focus group'
    ))
  ));
};

export const removeParticipant = participantId => (dispatch, getState) => {
  const { id } = idx(getState(), _ => _.project.focusGroups.viewState.currentGroup) || { id: null };
  const url = `/api/v1/focus-groups/${id}/participants/${participantId}/`;
  return request.delete(url).then(() =>
    dispatch(fetchGroup(id))
  ).catch(() => (
    dispatch(setBannerError(
      'Focus Group Error',
      'There was an error updating the participant for your focus group'
    ))
  ));
};

export const updateSettings = settings => (dispatch, getState) => {
  const { id } = idx(getState(), _ => _.project.focusGroups.viewState.currentGroup) || { id: null };
  const url = `/api/v1/focus-groups/${id}/`;

  return request.patch(url, settings).then(() =>
    dispatch(fetchGroup(id))
  ).catch(() => (
    dispatch(setBannerError(
      'Focus Group Error',
      'There was an error updating your focus group settings.'
    ))
  ));
};

/** ****************************************************************
 * Initial State *******************************************************
 */
export const initialState = {
  showAddModal: false,
  addModalStep: null,
  details: null,
  entries: [],
  selected: [],
  users: [],
  isCreating: false,
  isFetching: false,
  isFetchingAll: false,
  error: null,
  success: false,
  groups: [],
  currentGroup: null,
  showEditEntries: false,
  showAddParticipants: false,
  newParticipants: [],
};

/** ****************************************************************
 * Reducer *******************************************************
 */
const viewState = handleActions({
  [toggleAddModal]: state => ({
    ...initialState,
    groups: state.groups,
    showAddModal: !state.showAddModal,
    addModalStep: state.showAddModal ? null : 1
  }),
  [changeAddModalStep]: (state, action) => ({
    ...state,
    addModalStep: action.payload,
  }),
  [setDetails]: (state, action) => ({
    ...state,
    details: action.payload,
  }),
  [toggleSelectedEntries]: (state, action) => {
    const selected = state.selected.indexOf(action.payload) === -1 ? [
      ...state.selected, action.payload
    ] : state.selected.filter(id => (id !== action.payload));

    return {
      ...state,
      selected,
    };
  },
  [toggleEditEntries]: (state, action) => ({
    ...state,
    showEditEntries: !state.showEditEntries,
    selected: state.showEditEntries ? [] : action.payload,
  }),
  [toggleAddParticipants]: state => ({
    ...state,
    showAddParticipants: !state.showAddParticipants,
    newParticipants: [],
  }),
  [setEntries]: state => ({
    ...state,
    /* don't clear selected entries just yet if showing modal to add a FG, since user may get back
       to the entry selection step */
    selected: state.showAddModal? state.selected : [],
    entries: state.selected,
  }),
  [reqCreateGroup]: state => ({
    ...state,
    isCreating: true,
  }),
  [recCreateGroup]: () => ({
    ...initialState,
  }),
  [errCreateGroup]: (state, action) => ({
    ...state,
    isCreating: false,
    error: action.payload,
  }),
  [reqAllGroups]: state => ({
    ...state,
    isFetching: true,
  }),
  [recAllGroups]: (state, action) => ({
    ...initialState,
    isFetching: false,
    groups: action.payload,
  }),
  [reqFocusGroup]: state => ({
    ...state,
    isFetching: true,
    currentGroup: null,
  }),
  [recFocusGroup]: (state, action) => ({
    ...initialState,
    groups: state.groups,
    isFetching: false,
    currentGroup: action.payload,
  }),
}, initialState);

export default viewState;



// WEBPACK FOOTER //
// ./focus_groups/ducks/view_state.js